import React, { useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { AxiosError } from 'axios';
import { completePostSearchSurvey } from '../../api/postSearchSurvey';

type Stage = 'welcome' | 'question' | 'done';

const DID_HELP_GOAL_OPTIONS = [
  'Yes, completely',
  'Partially',
  'Not really',
  'No',
] as const;

const DISLIKED_OPTIONS_ALL = [
  'Not enough emails found',
  'Business categories are incorrect',
  'Confusing UI',
  'Slow results',
  'Needed API access',
  "Didn't trust the data yet",
  'Other',
] as const;

const NEEDS_GOAL_FOLLOW_UP = new Set<string>(['Partially', 'Not really', 'No']);

function needsGoalFollowUp(goal: string | null): boolean {
  return goal !== null && NEEDS_GOAL_FOLLOW_UP.has(goal);
}

const WOULD_PAY_FOR_OPTIONS_ALL = [
  'More verified emails',
  'LinkedIn/person data',
  'Cheaper pricing',
  'Pay-as-you-go credits',
  'API access',
  'Faster scraping',
  'CRM integrations',
  'AI outreach features',
  'Other',
] as const;

function shuffleArray<T>(items: readonly T[]): T[] {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const temporary = copy[index];
    copy[index] = copy[swapIndex];
    copy[swapIndex] = temporary;
  }
  return copy;
}

function shuffleWithOtherLast(items: readonly string[]): string[] {
  const withoutOther = items.filter((item) => item !== 'Other');
  const shuffled = shuffleArray(withoutOther);
  return items.includes('Other') ? [...shuffled, 'Other'] : shuffled;
}

const QUESTION_TOTAL = 4;

interface Props {
  onComplete: () => void;
  username?: string;
  completedSearchesCount: number;
}

export const PostSearchSurveyModal: React.FC<Props> = ({
  onComplete,
  username,
  completedSearchesCount,
}) => {
  const posthog = usePostHog();
  const [stage, setStage] = useState<Stage>('welcome');
  const [stepIdx, setStepIdx] = useState(0);
  const [didHelpGoal, setDidHelpGoal] = useState<string | null>(null);
  const [disliked, setDisliked] = useState<string[]>([]);
  const [wouldPayFor, setWouldPayFor] = useState<string[]>([]);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [followUpFeedback, setFollowUpFeedback] = useState('');
  const [dislikedOtherFeedback, setDislikedOtherFeedback] = useState('');
  const [wouldPayForOtherFeedback, setWouldPayForOtherFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const shuffledDislikedOptions = useMemo(
    () => shuffleWithOtherLast(DISLIKED_OPTIONS_ALL),
    [],
  );
  const shuffledWouldPayForOptions = useMemo(
    () => shuffleWithOtherLast(WOULD_PAY_FOR_OPTIONS_ALL),
    [],
  );

  const progressPct =
    stage === 'welcome'
      ? 0
      : stage === 'done'
        ? 100
        : Math.round(((stepIdx + 1) / QUESTION_TOTAL) * 100);

  const toggleMulti = (list: string[], value: string, setter: (next: string[]) => void) => {
    if (list.includes(value)) {
      setter(list.filter((item) => item !== value));
    } else {
      setter([...list, value]);
    }
  };

  const submitSurvey = async () => {
    if (!didHelpGoal || wouldPayFor.length === 0) {
      return;
    }
    if (needsGoalFollowUp(didHelpGoal) && followUpFeedback.trim() === '') {
      return;
    }
    if (disliked.includes('Other') && dislikedOtherFeedback.trim() === '') {
      return;
    }
    if (wouldPayFor.includes('Other') && wouldPayForOtherFeedback.trim() === '') {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const followUpForPayload = needsGoalFollowUp(didHelpGoal)
      ? followUpFeedback
      : null;
    const dislikedOtherForPayload = disliked.includes('Other')
      ? dislikedOtherFeedback
      : null;
    const wouldPayForOtherForPayload = wouldPayFor.includes('Other')
      ? wouldPayForOtherFeedback
      : null;
    const payload = {
      did_help_goal: didHelpGoal,
      follow_up_feedback: followUpForPayload,
      disliked,
      disliked_other_feedback: dislikedOtherForPayload,
      would_pay_for: wouldPayFor,
      would_pay_for_other_feedback: wouldPayForOtherForPayload,
      additional_feedback: additionalFeedback,
    };
    try {
      await completePostSearchSurvey(payload);
      const completedAt = new Date().toISOString();
      posthog?.capture('post_search_survey_submitted', {
        did_help_goal: payload.did_help_goal,
        follow_up_feedback: followUpForPayload,
        disliked: payload.disliked,
        disliked_other_feedback: dislikedOtherForPayload,
        would_pay_for: payload.would_pay_for,
        would_pay_for_other_feedback: wouldPayForOtherForPayload,
        additional_feedback: payload.additional_feedback,
        completed_searches_count: completedSearchesCount,
        $set: {
          post_search_survey_completed: true,
          post_search_survey_completed_at: completedAt,
        },
      });
      setStage('done');
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const detail = error.response?.data?.detail;
        const message =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg: string }).msg) : '')).join(' ')
              : 'Something went wrong. Please try again.';
        setSubmitError(message || 'Something went wrong. Please try again.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (stage === 'welcome') {
      setStage('question');
      setStepIdx(0);
      return;
    }
    if (stepIdx < QUESTION_TOTAL - 1) {
      setStepIdx((previous) => previous + 1);
      return;
    }
    await submitSurvey();
  };

  const handleBack = () => {
    if (stage === 'question') {
      if (stepIdx === 0) {
        setFollowUpFeedback('');
        setStage('welcome');
      } else {
        setStepIdx((previous) => previous - 1);
      }
    }
  };

  const canAdvance = (): boolean => {
    switch (stepIdx) {
      case 0:
        return (
          didHelpGoal !== null &&
          (!needsGoalFollowUp(didHelpGoal) || followUpFeedback.trim().length > 0)
        );
      case 1:
        return (
          !disliked.includes('Other') || dislikedOtherFeedback.trim().length > 0
        );
      case 2:
        return (
          wouldPayFor.length > 0 &&
          (!wouldPayFor.includes('Other') || wouldPayForOtherFeedback.trim().length > 0)
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderQuestion = () => {
    switch (stepIdx) {
      case 0:
        return (
          <div>
            <h2 className="text-2xl font-bold text-secondary leading-snug mb-6">
              Did WebLeads help you accomplish your goal?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DID_HELP_GOAL_OPTIONS.map((option) => {
                const isSelected = didHelpGoal === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setDidHelpGoal(option);
                      if (!needsGoalFollowUp(option)) {
                        setFollowUpFeedback('');
                      }
                    }}
                    className={[
                      'text-left p-4 rounded-xl border-2 transition-all duration-150 flex items-start gap-3 bg-white',
                      isSelected
                        ? 'border-primary shadow-[0_4px_16px_-8px_rgba(138,48,51,0.4)]'
                        : 'border-gray-200 hover:border-primary/40 hover:shadow-sm',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'mt-0.5 w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 transition-colors',
                        isSelected ? 'border-primary bg-primary' : 'border-gray-300',
                      ].join(' ')}
                    >
                      {isSelected && (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-3 h-3"
                        >
                          <path d="M5 12.5l4.5 4.5L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-secondary">{option}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <InlineFollowUpDrawer
              open={needsGoalFollowUp(didHelpGoal)}
              title="Tell us more"
              description="Briefly explain what didn't work or what you expected - it helps us improve."
              explanationText={followUpFeedback}
              placeholder="Your explanation..."
              onExplanationChange={setFollowUpFeedback}
            />
          </div>
        );
      case 1:
        return (
          <div>
            <h2 className="text-2xl font-bold text-secondary leading-snug mb-2">
              Anything you didn&apos;t like?
            </h2>
            <p className="text-sm text-gray-500 mb-6">Optional - select all that apply.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {shuffledDislikedOptions.map((option) => {
                const isSelected = disliked.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      if (option === 'Other' && disliked.includes('Other')) {
                        setDislikedOtherFeedback('');
                      }
                      toggleMulti(disliked, option, setDisliked);
                    }}
                    className={[
                      'text-left p-4 rounded-xl border-2 transition-all duration-150 flex items-start gap-3 bg-white',
                      isSelected
                        ? 'border-primary shadow-[0_4px_16px_-8px_rgba(138,48,51,0.4)]'
                        : 'border-gray-200 hover:border-primary/40 hover:shadow-sm',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'mt-0.5 w-5 h-5 rounded border-2 grid place-items-center shrink-0 transition-colors',
                        isSelected ? 'border-primary bg-primary' : 'border-gray-300',
                      ].join(' ')}
                    >
                      {isSelected && (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-3 h-3"
                        >
                          <path d="M5 12.5l4.5 4.5L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-secondary">{option}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <InlineFollowUpDrawer
              open={disliked.includes('Other')}
              title="Tell us what else"
              description="Briefly explain what you didn't like."
              explanationText={dislikedOtherFeedback}
              placeholder="What else did you notice?"
              onExplanationChange={setDislikedOtherFeedback}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <h2 className="text-2xl font-bold text-secondary leading-snug mb-2">
              What would make WebLeads worth paying for?
            </h2>
            <p className="text-sm text-gray-500 mb-6">Select all that apply.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {shuffledWouldPayForOptions.map((option) => {
                const isSelected = wouldPayFor.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      if (option === 'Other' && wouldPayFor.includes('Other')) {
                        setWouldPayForOtherFeedback('');
                      }
                      toggleMulti(wouldPayFor, option, setWouldPayFor);
                    }}
                    className={[
                      'text-left p-4 rounded-xl border-2 transition-all duration-150 flex items-start gap-3 bg-white',
                      isSelected
                        ? 'border-primary shadow-[0_4px_16px_-8px_rgba(138,48,51,0.4)]'
                        : 'border-gray-200 hover:border-primary/40 hover:shadow-sm',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'mt-0.5 w-5 h-5 rounded border-2 grid place-items-center shrink-0 transition-colors',
                        isSelected ? 'border-primary bg-primary' : 'border-gray-300',
                      ].join(' ')}
                    >
                      {isSelected && (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-3 h-3"
                        >
                          <path d="M5 12.5l4.5 4.5L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-secondary">{option}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <InlineFollowUpDrawer
              open={wouldPayFor.includes('Other')}
              title="Tell us what else"
              description="Briefly explain what would make WebLeads worth paying for."
              explanationText={wouldPayForOtherFeedback}
              placeholder="What else would make it worth paying for?"
              onExplanationChange={setWouldPayForOtherFeedback}
            />
          </div>
        );
      case 3:
        return (
          <div>
            <h2 className="text-2xl font-bold text-secondary leading-snug mb-2">
              Anything you would like to add?
            </h2>
            <p className="text-sm text-gray-500 mb-4">Optional — share any extra context.</p>
            <textarea
              value={additionalFeedback}
              onChange={(event) => setAdditionalFeedback(event.target.value)}
              rows={5}
              className="w-full rounded-xl border-2 border-gray-200 p-4 text-sm text-secondary focus:border-primary focus:outline-none focus:ring-0 resize-y min-h-[120px]"
              placeholder="Type here…"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-black/40 backdrop-blur-md">
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-8 pt-6 pb-0 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center font-bold text-sm">
              W
            </div>
            <span className="text-sm font-semibold text-secondary">WebLeads</span>
            {stage === 'question' && (
              <span className="ml-2 text-xs text-gray-400">
                Step {stepIdx + 1} of {QUESTION_TOTAL}
              </span>
            )}
          </div>
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {stage === 'welcome' && (
            <WelcomeSurveyScreen
              onStart={handleNext}
              username={username}
              completedSearchesCount={completedSearchesCount}
            />
          )}
          {stage === 'question' && (
            <div>
              {submitError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {submitError}
                </div>
              )}
              {renderQuestion()}
            </div>
          )}
          {stage === 'done' && <DoneSurveyScreen onComplete={onComplete} />}
        </div>

        {stage === 'question' && (
          <div className="flex items-center gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50/60 shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-secondary px-3 py-2 rounded-lg hover:bg-white transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
              >
                <path d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="hidden sm:flex items-center gap-1.5 ml-2">
              {Array.from({ length: QUESTION_TOTAL }).map((_, index) => (
                <div
                  key={index}
                  className={[
                    'h-1.5 rounded-full transition-all',
                    index < stepIdx
                      ? 'w-6 bg-primary'
                      : index === stepIdx
                        ? 'w-10 bg-primary'
                        : 'w-6 bg-gray-200',
                  ].join(' ')}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                void handleNext();
              }}
              disabled={!canAdvance() || submitting}
              className={[
                'ml-auto flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-xl transition-all',
                canAdvance() && !submitting
                  ? 'bg-primary hover:bg-primary-hover text-white shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed',
              ].join(' ')}
            >
              {submitting ? 'Saving…' : stepIdx === QUESTION_TOTAL - 1 ? 'Finish' : 'Next'}
              {!submitting && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function WelcomeSurveyScreen({
  onStart,
  username,
  completedSearchesCount,
}: {
  onStart: () => void;
  username?: string;
  completedSearchesCount: number;
}) {
  const displayName = username
    ? username.charAt(0).toUpperCase() + username.slice(1)
    : 'there';

  return (
    <div className="py-4">
      <div className="flex items-center gap-2 mb-6 text-xs font-semibold tracking-widest uppercase text-gray-400">
        <svg
          className="w-3.5 h-3.5 animate-spin text-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
        Quick feedback · {completedSearchesCount} completed searches
      </div>
      <h2 className="text-3xl font-bold text-secondary leading-snug mb-2">Thanks, {displayName}.</h2>
      <h2 className="text-3xl font-bold leading-snug mb-5">
        <span className="text-secondary">Help us make WebLeads </span>
        <span className="text-primary">work better for you.</span>
      </h2>
      <p className="text-gray-500 text-[15px] leading-relaxed mb-8 max-w-md">
        Four short questions about your experience so far. It takes about a minute.
      </p>
      <button
        type="button"
        onClick={onStart}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md transition-all"
      >
        Let&apos;s go
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

function DoneSurveyScreen({ onComplete }: { onComplete: () => void }) {
  return (
    <div className="py-4 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary text-white grid place-items-center mx-auto mb-5">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-7 h-7"
        >
          <path d="M5 12.5l4.5 4.5L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-secondary mb-2">Thank you.</h2>
      <p className="text-gray-500 text-sm mb-8 max-w-sm mx-auto">
        Your feedback helps us improve WebLeads.
      </p>
      <button
        type="button"
        onClick={onComplete}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md transition-all"
      >
        Continue
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

function InlineFollowUpDrawer({
  open,
  title,
  description,
  explanationText,
  placeholder,
  onExplanationChange,
}: {
  open: boolean;
  title: string;
  description: string;
  explanationText: string;
  placeholder: string;
  onExplanationChange: (value: string) => void;
}) {
  return (
    <div
      className={[
        'overflow-hidden transition-all duration-300 ease-out',
        open ? 'max-h-[260px] opacity-100 mt-5' : 'max-h-0 opacity-0 mt-0',
      ].join(' ')}
      aria-hidden={!open}
    >
      <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
        <div className="mb-3">
          <h3 className="text-sm font-bold text-secondary">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {description}
          </p>
        </div>
        <textarea
          value={explanationText}
          onChange={(event) => onExplanationChange(event.target.value)}
          rows={4}
          className="w-full rounded-xl border-2 border-gray-200 bg-white p-4 text-sm text-secondary focus:border-primary focus:outline-none focus:ring-0 resize-y min-h-[110px]"
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

export default PostSearchSurveyModal;
